import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { ReportResultBaseModel } from '@treasury/domain/reports/report-results.entity';
import { ReportingService } from '@treasury/domain/reports/reports.service';
import { TmContainer } from '@treasury/presentation';
import {
    download,
    ellipsisIcon,
    filePlusIcon,
    transferIcon,
} from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-body';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-context-menu';
import '@treasury/presentation/components/tm-icon';
import '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-summary-tile';
import { css, html, nothing, type TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { filterTableResults } from '../../../utilities/table-result-filter';
import { ReportAction } from '../../dashboard/components/reports-widget';
import { HeaderBarService } from '../../services/jhd-header-bar.service';
import { BalanceReportItemVm } from '../balance/balance-report-item.vm';
import { reportFilterFields } from '../data/report-filter-fields';
import { ReportFiltersVm } from '../data/report-filters.vm';
import { ReportMetaDataVm } from '../data/report-meta-data.vm';
import { EdiReportItemVm } from '../edi/edi-report-item.vm';
import { TransactionReportItemVm } from '../transaction/transaction-report-item.vm';
import './report-filters';
import './report-summary';
import './report-table';

export enum ReportActions {
    DOWNLOAD_REPORT = 'Download',
    SAVE_REPORT = 'Save',
    SWITCH_REPORT = 'Switch Report',
}

export const tagName = 'report-layout';
@customElement(tagName)
export class ReportLayout extends TmContainer {
    @property({ type: String })
    public filterText = '';

    @InjectProperty()
    declare private reportingService: ReportingService;

    @InjectProperty()
    declare private navigationService: NavigationService;

    @InjectProperty()
    declare private headerBarService: HeaderBarService;

    @state()
    private filtersVisible = screen.width >= 1024;

    @state()
    private reportId = '';

    @state()
    private reportType = '';

    @state()
    private reportMetaData?: ReportMetaDataVm;

    @state()
    private reportFilters: ReportFiltersVm = new ReportFiltersVm();

    @state()
    private reportResults!: ReportResultBaseModel;

    @state()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private reportRecords: any[] = [];

    @state()
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private filteredRecords: any[] = [];

    @state()
    private saveReportModalOpen = false;

    @state()
    private formattedRecords:
        | BalanceReportItemVm[]
        | TransactionReportItemVm[]
        | EdiReportItemVm[] = [];

    @state()
    private tableLoading = false;

    @state()
    private defaultHeaderConfig = {
        title: 'Reports',
        menuItems: this.getHeaderMenuItems(),
        backAction: () => this.navigationService.navigate(`/dashboard`),
    };

    public connectedCallback(): void {
        super.connectedCallback();
        this.headerBarService.configure(this.defaultHeaderConfig);
    }

    private get downloadOptions() {
        if (!this.reportResults) {
            return [];
        }
        return this.reportResults.downloadOptions.map((option: string) => {
            return {
                text: option,
            };
        });
    }

    public async firstUpdated() {
        const { params } = await this.navigationService.getRouteData<{
            type: string;
            id: string;
            action: ReportAction;
        }>();
        this.reportId = params.id;
        this.reportType = params.type;
        await this.tryFetch(
            () =>
                Promise.all([
                    this.reportingService.getReportMetaDataAndFilters(this.reportId),
                    this.reportingService.getReportTransactionCodes(),
                ]),
            ([data, paymentTypes]) => {
                this.reportMetaData = new ReportMetaDataVm(data.metaData);

                this.headerBarService.configure({
                    ...this.defaultHeaderConfig,
                    title: this.reportMetaData.name,
                });
                this.reportFilters = new ReportFiltersVm(data.filters.toDto());
                this.reportFilters.paymentTypes = paymentTypes;
            },
            () => {
                this.notificationService.renderError('Failed to fetch report metadata');
            }
        );

        if (params.action === ReportAction.RUN_REPORT) {
            this.runReport(this.reportFilters);
        }

        this.scrollTmBodyIntoView();
    }

    public render() {
        if (!this.reportMetaData) {
            return nothing;
        }

        return html`<tm-section
                id="report-display"
                class=${classMap({
                    'flex flex-col border-l border-b': true,
                    'overflow-hidden h-full': !this.isDesktop,
                    'mx-2 mb-12 mt-2 rounded-lg h-[92%] pb-[25px]': this.isDesktop,
                })}
                .shadow=${this.deviceService.isDesktop}
                .padding=${this.deviceService.isDesktop}
            >
                <!-- filters -->
                <report-filters
                    class=${classMap({
                        'rounded-t': this.deviceService.isDesktop,
                    })}
                    .asDesktop=${this.deviceService.isDesktop}
                    .loading=${this.loading}
                    .filtersVisible=${this.filtersVisible}
                    .report=${this.reportMetaData}
                    .filters=${this.reportFilters}
                    @filter=${(e: CustomEvent) => {
                        this.reportFilters = e.detail.filters;
                        this.runReport(e.detail.filters);
                    }}
                    @filters-closed=${() => {
                        this.filtersVisible = false;
                    }}
                ></report-filters>
                <!-- Report summary -->
                <report-summary
                    .report=${this.reportResults}
                    @filter-tile-changed=${(e: CustomEvent) => {
                        this.localFilterRecords(e.detail);
                    }}
                ></report-summary>
                <!-- tm-list-header -->
                <tm-list-header
                    .filteringEnabled=${true}
                    .filterButton=${true}
                    @filterButtonClick=${() => {
                        this.filtersVisible = !this.filtersVisible;
                    }}
                    @search-term-change=${(e: CustomEvent) => {
                        this.filterText = e.detail.value;
                        this.filteredRecords = filterTableResults(
                            e,
                            this.formattedRecords,
                            reportFilterFields
                        );
                    }}
                >
                </tm-list-header>
                <!-- Report content -->
                <div id="report-table-wrapper">
                    <report-table
                        .tableLoading=${this.tableLoading}
                        .records=${this.filteredRecords}
                        .report=${this.reportMetaData}
                    ></report-table>
                </div>
            </tm-section>
            <!-- Save report modal -->
            <report-save-custom
                .open=${this.saveReportModalOpen}
                .reportMetaData=${this.reportMetaData}
                .reportFilters=${this.reportFilters}
                .reportId=${this.reportMetaData.id}
                @close=${() => {
                    this.saveReportModalOpen = false;
                }}
            ></report-save-custom>`;
    }

    private async runReport(filters: ReportFiltersVm) {
        if (!this.reportMetaData) {
            return;
        }
        this.tableLoading = true;
        try {
            const result = await this.reportingService.runReport(filters);
            this.reportResults = result;
            this.reportRecords = result.data;
            this.getFormattedRecords();
            this.headerBarService.configure({
                ...this.defaultHeaderConfig,
                title: this.reportMetaData.name,
                subtitle: this.reportResults.subtitle,
                menuItems: this.getHeaderMenuItems(),
            });
        } catch {
            this.notificationService.renderError('Failed to run report');
        } finally {
            this.tableLoading = false;
        }
    }

    private getFormattedRecords() {
        if (!this.reportRecords.length || !this.reportResults) {
            return;
        }

        if (this.reportMetaData?.reportType?.toLowerCase() === 'balance') {
            this.formattedRecords = this.reportRecords.map(
                record => new BalanceReportItemVm(record)
            );
            this.filteredRecords = this.formattedRecords;
        } else if (this.reportMetaData?.reportType?.toLowerCase() === 'edi') {
            this.formattedRecords = this.reportRecords.map(record => new EdiReportItemVm(record));
            this.filteredRecords = this.formattedRecords;
        } else {
            this.formattedRecords = this.reportRecords.map(
                record => new TransactionReportItemVm(record)
            );
            this.filteredRecords = this.formattedRecords;
        }
    }

    private async downloadReport(option: string) {
        this.reportingService.downloadReport(this.reportFilters, this.reportResults, option);
    }

    private localFilterRecords(filters: { key: string; value: string | number }) {
        if (!filters) {
            this.filteredRecords = this.formattedRecords;
            return;
        }
        const { key, value } = filters;
        this.filteredRecords = this.formattedRecords;
        this.filteredRecords = this.filteredRecords.filter(record => {
            return record[key] === value;
        });
    }

    private isDownloadOption(option: string) {
        return this.reportResults.downloadOptions.includes(option);
    }

    private getHeaderMenuItems() {
        return [
            {
                icon: html`<tm-context-menu
                    .items=${[
                        {
                            component: createActionItem(filePlusIcon, ReportActions.SAVE_REPORT),
                        },
                        {
                            component: createActionItem(download, ReportActions.DOWNLOAD_REPORT),
                            disabled: !this.downloadOptions.length,
                            children: [...this.downloadOptions],
                        },
                        {
                            component: createActionItem(transferIcon, ReportActions.SWITCH_REPORT),
                        },
                    ]}
                    .openOn=${'click'}
                    @item-selected=${(e: CustomEvent) => {
                        const action = e.detail.value.text
                            ? e.detail.value.text
                            : e.detail.value.component.textContent;
                        if (action === ReportActions.SWITCH_REPORT) {
                            this.dispatchEvent(
                                new CustomEvent('switch-report', { bubbles: true, composed: true })
                            );
                        } else if (action === ReportActions.SAVE_REPORT) {
                            this.saveReportModalOpen = true;
                        } else {
                            this.downloadReport(action);
                        }
                    }}
                    ><tm-button importance="tertiary"
                        ><tm-icon .icon=${ellipsisIcon} color="white"></tm-icon></tm-button
                ></tm-context-menu>`,
                title: '',
                visible: () => screen.width < 1024,
                action: () => undefined,
            },
        ];
    }

    private scrollTmBodyIntoView() {
        const tmBody = this.shadowRoot?.querySelector('tm-body');
        if (tmBody) {
            tmBody.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
    }

    public static get styles() {
        return css`
            :host {
                display: block;
            }
        `;
    }
}

function createActionItem(iconName: TemplateResult, text: string) {
    const item = document.createElement('vaadin-context-menu-item');
    const wrapper = document.createElement('div');
    const icon = document.createElement('tm-icon');

    icon.style.marginRight = '5px';
    icon.icon = iconName;
    wrapper.classList.add('flex', 'items-center');
    wrapper.appendChild(icon);
    item.appendChild(wrapper);
    if (text) {
        wrapper.appendChild(document.createTextNode(text));
    }
    return item;
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ReportLayout;
    }
}
